/**
 * rule-action-compat — which rule actions may combine, and why not.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Split out of rule-action-meta so each file stays inside the size limit: this
 * one holds the compatibility MODEL, that one the labels and glyphs.
 */

import type { RuleAction } from '@trafficpulse/shared';

import { RULE_ACTIONS, RULE_ACTION_META } from './rule-action-meta';

/**
 * Logical compatibility model for a rule group's actions. A group does exactly
 * ONE primary verb — `exclude` (freeze/ignore) XOR `weight` (pin/boost the
 * matched share) XOR a rotation verb — and `add-element` is a backfill
 * MODIFIER that is only meaningful alongside a rotation verb. `weight` runs
 * alone because every combination contradicts it: `exclude` freezes the row
 * OUT of the pool a weight rule shapes, and the rotation verbs take the
 * element OFF the tracker so there is no weight left to hold. This mirrors
 * the engine (eliminate wins over set-to-queue; add-element alone is inert)
 * so the picker can't produce a combination that does nothing or contradicts
 * itself.
 */
export const ROTATION_VERBS: readonly RuleAction[] = [
  'set-to-queue',
  'set-to-waiting',
  'eliminate',
  // MD-1147: the cap verbs are rotation verbs too — one destination per rule.
  'pause',
  'reactivate',
];

/**
 * The queue-SYSTEM actions (rotation lifecycle). Gated "available soon" for
 * landers/offers during the paths-focused launch — a rule targeting those layers
 * can only use `exclude` (weight), never these. Empty QUEUE-layer gating in
 * rule-layers-launch.ts to re-enable. `exclude` is intentionally NOT here.
 */
export const QUEUE_LIFECYCLE_ACTIONS: readonly RuleAction[] = [
  'set-to-queue',
  'set-to-waiting',
  'eliminate',
  'pause',
  'reactivate',
  'add-element',
];
export const PRIMARY_VERBS: readonly RuleAction[] = [
  'exclude',
  'weight',
  'set-to-queue',
  'set-to-waiting',
  'eliminate',
  'pause',
  'reactivate',
];

const isRotationVerb = (a: RuleAction): boolean => ROTATION_VERBS.includes(a);
const hasRotationVerb = (actions: RuleAction[]): boolean =>
  actions.some(isRotationVerb);

/** Re-order (and dedupe) a set into the canonical picker order — keeps the
 *  serialized `actions` array stable for round-trips + cross-layer merging. */
function inActionOrder(actions: RuleAction[]): RuleAction[] {
  return RULE_ACTIONS.map((a) => a.value).filter((v) => actions.includes(v));
}

/**
 * The next VALID action set after toggling `action`. Every INCOMPATIBLE action
 * is greyed out in the picker (see `isActionDisabled`), so a bad combo can't be
 * ticked; this only has to keep the state consistent: checking `exclude` yields
 * exactly `[exclude]`; checking a rotation verb clears the other primaries but
 * keeps `add-element`; UN-checking a rotation verb also drops its now-orphaned
 * `add-element`. Clearing the last action → `[]` (empty just re-opens the picker
 * — it serializes back to `exclude`).
 */
export function resolveActionToggle(
  current: RuleAction[],
  action: RuleAction,
  checked: boolean,
): RuleAction[] {
  if (!checked) {
    const next = current.filter((v) => v !== action);
    // Dropping a rotation verb orphans its add-element modifier → drop it too.
    return inActionOrder(
      isRotationVerb(action) && !hasRotationVerb(next)
        ? next.filter((v) => v !== 'add-element')
        : next,
    );
  }
  if (action === 'exclude') return ['exclude']; // Exclude always runs alone.
  if (action === 'weight') return ['weight']; // Weight always runs alone too.
  if (isRotationVerb(action)) {
    const keepAdd = current.includes('add-element');
    return inActionOrder(keepAdd ? [action, 'add-element'] : [action]);
  }
  return inActionOrder([...current, action]); // add-element modifier
}

/**
 * Actions greyed out for the CURRENT selection — so incompatible actions are
 * never on offer: `soon` placeholders; `exclude` once a rotation verb is chosen;
 * the rotation verbs while `exclude` is chosen; `set-to-queue`/`eliminate` while
 * the OTHER rotation verb is chosen (mutually exclusive); `add-element` while
 * `exclude` is chosen or no rotation verb is present. A currently-checked action
 * is never disabled (so it can always be un-checked to switch).
 */
export function isActionDisabled(
  current: RuleAction[],
  action: RuleAction,
): boolean {
  if (RULE_ACTION_META[action]?.soon) return true;
  const hasExclude = current.includes('exclude');
  const hasWeight = current.includes('weight');
  switch (action) {
    case 'exclude':
      return hasWeight || hasRotationVerb(current);
    case 'weight':
      return hasExclude || hasRotationVerb(current) || current.includes('add-element');
    case 'set-to-queue':
    case 'set-to-waiting':
    case 'eliminate':
    case 'pause':
    case 'reactivate':
      // The rotation verbs are mutually exclusive — one destination per rule.
      return (
        hasExclude ||
        hasWeight ||
        current.some((v) => isRotationVerb(v) && v !== action)
      );
    case 'add-element':
      // Reactivate brings elements BACK — there is no freed slot to backfill.
      return (
        hasExclude ||
        hasWeight ||
        !hasRotationVerb(current) ||
        current.includes('reactivate')
      );
    default:
      return false;
  }
}
